import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { getCourse, listCompletedLessons, type Course, type LessonSummary } from "@/lib/courses";
import { CheckCircle2, Lock, Clock, ChevronRight } from "lucide-react";

export const Route = createFileRoute("/courses/$courseSlug/")({
  component: CourseDetailPage,
  head: ({ params }) => ({
    meta: [
      { title: `Course: ${params.courseSlug.replace(/-/g, " ")} - Inquira` },
      { name: "description", content: "A guided Inquira study path: short lessons, practice questions and a quiz after each one." },
      { property: "og:title", content: "Inquira study path" },
      { property: "og:description", content: "Work through short lessons with practice and quizzes, at your own pace." },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function CourseDetailPage() {
  const { courseSlug } = Route.useParams();
  const { t } = useTranslation();
  const { user, isPaid } = useAuth();
  const [course, setCourse] = useState(null as Course | null);
  const [lessons, setLessons] = useState([] as LessonSummary[]);
  const [done, setDone] = useState(new Set() as Set<string>);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    getCourse(courseSlug)
      .then((loaded) => {
        if (!loaded) { setMissing(true); return; }
        setCourse(loaded.course);
        setLessons(loaded.lessons);
      })
      .catch(() => setMissing(true));
  }, [courseSlug]);

  useEffect(() => {
    if (!user) { setDone(new Set()); return; }
    listCompletedLessons(user.id).then(setDone).catch(() => setDone(new Set()));
  }, [user]);

  if (missing) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-10">
        <p className="text-muted-foreground">{t("courses.notFound")}</p>
        <Link to="/courses" className="mt-3 inline-block text-primary underline">{t("courses.allCourses")}</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8 space-y-8">
      <header className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{course?.subject}</p>
        <h1 className="text-3xl font-bold">{course?.title}</h1>
        <p className="text-muted-foreground">{course?.summary}</p>
        <p className="text-sm text-muted-foreground">
          {t("courses.lessonCount", { count: lessons.length })}
        </p>
      </header>

      <ol className="space-y-3">
        {lessons.map((lesson) => {
          const locked = !lesson.is_free && !isPaid;
          return (
            <li key={lesson.id}>
              <Link
                to="/courses/$courseSlug/$lessonSlug"
                params={{ courseSlug, lessonSlug: lesson.slug }}
                className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 transition hover:border-primary"
              >
                <span className="grid size-8 shrink-0 place-items-center rounded-full bg-muted text-sm font-semibold">
                  {lesson.position}
                </span>
                <span className="flex-1">
                  <span className="block font-semibold">{lesson.title}</span>
                  <span className="block text-sm text-muted-foreground">{lesson.objective}</span>
                  <span className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="size-3" aria-hidden="true" />
                    {t("courses.minutes", { count: lesson.minutes })}
                    {lesson.is_free && <span>· {t("common.free")}</span>}
                  </span>
                </span>
                {done.has(lesson.id) ? (
                  <CheckCircle2 className="size-5 shrink-0 text-primary" aria-label={t("courses.completed")} />
                ) : locked ? (
                  <Lock className="size-4 shrink-0 text-muted-foreground" aria-label={t("courses.premiumOnly")} />
                ) : (
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground rtl:rotate-180" aria-hidden="true" />
                )}
              </Link>
            </li>
          );
        })}
      </ol>

      <Link to="/courses" className="inline-block text-sm text-primary underline">{t("courses.allCourses")}</Link>
    </div>
  );
}
